import { useCallback } from 'react';
import { Recipient, Segment } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { FilterOptions } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/bulk.pb';
import { type UpsertAudience, useCreateSegment, useDeleteSegment, useListSegments } from '@frontend/api-bulk-messaging';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../bulk-message-editor.context';
import { useConvertScheduledCampaignToDraft } from './use-convert-scheduled-campaign-to-draft';

export const useAudienceManagement = () => {
  const { t } = useTranslation('bulk-messaging');
  const alert = useAlert();
  const { selectedOrgId: orgId } = useAppScopeStore();
  const { campaignId, getCampaignData, locationIds } = useBulkMessageEditorContext();
  const campaign = getCampaignData();

  const { data: segments = [], isFetching: isFetchingSegments } = useListSegments({
    campaignId,
    orgId,
  });
  const { mutateAsync: createSegment, isLoading: isCreatingSegment } = useCreateSegment();
  const { mutateAsync: deleteSegment, isLoading: isDeletingSegment } = useDeleteSegment();

  const convertScheduledCampaignToDraft = useConvertScheduledCampaignToDraft(orgId, campaign);

  const deleteSegments = useCallback(
    async (segmentsToDelete: Segment[]) => {
      if (segmentsToDelete.length === 0) return;
      await convertScheduledCampaignToDraft();
      await Promise.all(
        segmentsToDelete.map((segment) =>
          deleteSegment({
            campaignId,
            segmentId: segment.segmentId,
            orgId,
          })
        )
      );
    },
    [convertScheduledCampaignToDraft, deleteSegment, campaignId, orgId]
  );

  const deleteAudience = useCallback(async () => {
    await deleteSegments(segments);
  }, [deleteSegments, segments]);

  const createListSegments = useCallback(
    async (listIdsToCreate: string[]) => {
      if (listIdsToCreate.length === 0) return;
      await convertScheduledCampaignToDraft();
      await Promise.all(
        listIdsToCreate.map((listId) =>
          createSegment({
            campaignId,
            orgId,
            segment: { segmentId: '', listId },
          })
        )
      );
    },
    [convertScheduledCampaignToDraft, createSegment, campaignId, orgId]
  );

  const upsertRecipients = useCallback(
    async (recipientsToUpsert: Recipient[]) => {
      await deleteAudience();
      await createSegment({
        campaignId,
        orgId,
        segment: { segmentId: '', recipients: recipientsToUpsert },
      });
    },
    [deleteAudience, createSegment, campaignId, orgId]
  );

  const upsertLists = useCallback(
    async (listIdsToUpsert: string[]) => {
      const segmentsToDelete = segments.filter(
        (segment) => !(segment.filterOptions || (segment.listId && listIdsToUpsert.includes(segment.listId)))
      );
      await deleteSegments(segmentsToDelete);

      const segmentsToCreate = listIdsToUpsert.filter(
        (listId) => !segments.find((segment) => segment.listId === listId)
      );
      await createListSegments(segmentsToCreate);
    },
    [segments, deleteSegments, createListSegments]
  );

  // TODO: Temporary workaround until the old audience tray is removed
  const upsertListsWithoutDiff = useCallback(
    async (listIdsToUpsert: string[]) => {
      const segmentsToCreate = listIdsToUpsert.filter(
        (listId) => !segments.find((segment) => segment.listId === listId)
      );
      await createListSegments(segmentsToCreate);
    },
    [segments, createListSegments]
  );

  const upsertFilters = useCallback(
    async (filtersToUpsert: FilterOptions) => {
      await convertScheduledCampaignToDraft();
      await createSegment({
        campaignId,
        orgId,
        segment: {
          segmentId: '',
          filterOptions: filtersToUpsert,
        },
      });
    },
    [convertScheduledCampaignToDraft, createSegment, campaignId, orgId]
  );

  const alertUnexpectedError = useCallback(
    (isUpdating: boolean) => {
      alert.error(
        isUpdating
          ? t('An unexpected error occurred while updating your audience. Please try again or contact support.')
          : t('An unexpected error occurred while creating your audience. Please try again or contact support.')
      );
    },
    [alert, t]
  );

  const upsertAudience: UpsertAudience = useCallback(
    async ({ filters, listIds, recipients, diffLists = true }) => {
      if (locationIds.length === 0) return;
      const isUpdating = segments.length > 0;
      if (!campaignId) {
        alertUnexpectedError(isUpdating);
        return;
      }

      if (recipients && recipients.length > 0) {
        await upsertRecipients(recipients);
      } else if (listIds && listIds.length > 0) {
        if (diffLists) await upsertLists(listIds);
        else await upsertListsWithoutDiff(listIds); // TODO: Only needed until old audience tray is removed
      } else if (filters) {
        await upsertFilters(filters);
      } else {
        alertUnexpectedError(isUpdating);
        return;
      }

      alert.success(isUpdating ? t('Audience successfully updated!') : t('Audience successfully created!'));
    },
    [
      alert,
      alertUnexpectedError,
      campaignId,
      locationIds.length,
      segments.length,
      t,
      upsertFilters,
      upsertLists,
      upsertListsWithoutDiff,
      upsertRecipients,
    ]
  );

  return {
    deleteAudience,
    deleteSegments,
    isProcessingAudience: isFetchingSegments || isCreatingSegment || isDeletingSegment,
    segmentCount: segments.length,
    upsertAudience,
  };
};
